/**
 * Settings › Profile & branding — `organization-build-book.md` §6.1,
 * `org-01-profile-branding.png`.
 *
 * Who the company is, everywhere a customer sees it: the name on a proposal,
 * the colour of the button in an email, the address at the bottom of an
 * invoice, the mark on the portal's sign-in page.
 *
 * The rail on the right re-renders from the form on every keystroke. That is
 * the point of the screen — a branding page that makes you save and then go
 * and find a proposal to see what you did is a page people change once and
 * never touch again.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  BRAND_PRESETS, DEFAULT_BRAND_COLOR, can, contrastOnWhite,
  type CompanyProfileView, type LogoSlot, type MeResponse,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request, uploadLogo } from '../lib/api.js'
import { Band, Button, Segmented } from '../components/primitives.js'
import { SettingsCard } from '../components/settings-card.js'
import {
  CheckRow, Field, FormGrid, PhoneInput, ReadonlyInput, RegionSelect, SelectInput, TextArea,
  TextInput, Wide,
} from '../components/form/index.js'
import { IconCheck, IconEmail, IconExternal, IconLock, IconWarn } from '../frame/icons.js'
import { useSettingsBar } from '../settings/settings-bar.js'

/** WCAG's threshold for body text. Below it, the colour is for bands only. */
const TEXT_SAFE = 4.5

/**
 * What to call a field in the banner when the server refuses it.
 *
 * The server's message says what is wrong ("Eleven characters at most.") and
 * not what it is wrong *about*, which on a page of five cards is half an
 * answer. Named here rather than derived from the key, because "smsSenderName"
 * is not what the label above the box says.
 */
const LABELS: Partial<Record<string, string>> = {
  displayName: 'Display name', legalName: 'Legal name', tagline: 'Tagline',
  website: 'Website', brandColor: 'Brand colour',
  addressLine1: 'Address', addressLine2: 'Address', city: 'City', state: 'State',
  postalCode: 'ZIP', phone: 'Phone', email: 'Email',
  licenseNumber: 'License / registration #',
  docHeader: 'Header line', docFooter: 'Footer', paperSize: 'Paper size',
  emailFromName: 'Email from name', replyTo: 'Reply-to',
  smsSenderName: 'Text sender name',
  dateFormat: 'Dates', weekStart: 'Week starts', units: 'Measurements',
}

export function CompanyProfilePage({ me }: { me: MeResponse }) {
  const [saved, setSaved] = useState<CompanyProfileView | null>(null)
  const [draft, setDraft] = useState<CompanyProfileView | null>(null)
  const [error, setError] = useState<{ field?: string; message: string } | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const canEdit = can(me.permissions, 'company_settings', 'edit')

  const load = useCallback(async () => {
    try {
      const one = await request<CompanyProfileView>('/company/profile')
      setSaved(one)
      setDraft(one)
      setError(null)
    } catch (problem) {
      setError({ message: (problem as { message?: string }).message ?? 'That did not load.' })
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(saved),
    [draft, saved])

  const set = <K extends keyof CompanyProfileView>(key: K, value: CompanyProfileView[K]) =>
    setDraft((was) => (was ? { ...was, [key]: value } : was))

  const save = useCallback(async () => {
    if (!draft) return
    setBusy(true)
    setError(null)
    try {
      const { logoUrl, logoDarkUrl, markUrl, currency, timezone, version, ...body } = draft
      void logoUrl; void logoDarkUrl; void markUrl; void currency; void timezone; void version
      const answer = await request<CompanyProfileView>('/company/profile',
        { method: 'PUT', body })
      setSaved(answer)
      setDraft(answer)
      setNotice('Saved. Every document and email uses it from now on.')
    } catch (problem) {
      const failure = problem as { field?: string; message?: string }
      setError({ field: failure.field, message: failure.message ?? 'That did not save.' })
    } finally {
      setBusy(false)
    }
  }, [draft])

  /* ⌘↵ saves, the way every other form here does. */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey) && dirty) {
        event.preventDefault()
        void save()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [dirty, save])

  const barActions = useSettingsBar({
    title: 'Profile & branding',
    sub: 'what customers and your team see on every document, email and the portal',
    actions: canEdit
      ? (
        <Button
          variant="primary"
          className="h-8 text-[12.5px]"
          disabled={!dirty || busy}
          onClick={() => void save()}
        >
          {busy ? 'Saving…' : 'Save changes'}
        </Button>
      )
      : undefined,
  })

  if (!draft) {
    return (
      <div className="p-5">
        {barActions}
        <p className="m-0 text-body text-text-muted">{error?.message ?? 'Loading…'}</p>
      </div>
    )
  }

  const validHex = /^#[0-9A-Fa-f]{6}$/.test(draft.brandColor)
  const ratio = contrastOnWhite(draft.brandColor)
  const textSafe = ratio >= TEXT_SAFE

  /*
   * Which field the server refused, if any.
   *
   * Every field asks, because the alternative is what this page did: the
   * banner was drawn only for an error with *no* field, and only two fields
   * rendered one of their own. So a name over 120 characters, a text sender
   * over eleven, a half-typed colour — each of them failed the whole save,
   * said nothing anywhere, and the next page load showed the old values back.
   * One bad field loses the other four cards with it, which is exactly what
   * "I changed it, saved, and it reverted" looks like from the outside.
   */
  const errorFor = (key: keyof CompanyProfileView) =>
    (error?.field === key ? error.message : undefined)

  return (
    <div className="flex items-start gap-5 p-5 max-xl:flex-col">
      {barActions}

      <div className="flex min-w-0 max-w-[860px] flex-1 flex-col gap-4 max-xl:max-w-full">
        {/*
          * Above the cards, not below them. The Save button is in the page bar
          * at the top; a message about what the save did, eight hundred pixels
          * under the fold, is a message nobody reads.
          */}
        {notice && <Band tone="green" icon={<IconCheck size={15} />}>{notice}</Band>}
        {error && (
          <Band tone="amber" icon={<IconWarn size={15} />}>
            {error.field ? `${LABELS[error.field] ?? error.field}: ` : ''}{error.message}
          </Band>
        )}

        <SettingsCard title="Identity" sub="what customers and your team see">
          <div className="flex flex-col gap-3.5 px-[18px] py-4">
            <div className="flex flex-wrap items-start gap-[18px]">
              <LogoSlotView
                slot="light" label="Logo" width={180} height={92}
                url={draft.logoUrl} canEdit={canEdit} onUploaded={() => void load()}
                sub="on proposals, invoices and the portal"
              />
              <LogoSlotView
                slot="dark" label="Logo on dark" width={180} height={92} dark
                url={draft.logoDarkUrl} canEdit={canEdit} onUploaded={() => void load()}
                sub="for dark backgrounds and the crew iPad"
              />
              <LogoSlotView
                slot="mark" label="Mark" width={92} height={92}
                url={draft.markUrl} canEdit={canEdit} onUploaded={() => void load()}
                sub="the square one, for avatars and tabs"
              />
            </div>

            <FormGrid>
              <Field
                label="Display name"
                required
                hint="on every document, email and the portal"
                error={errorFor('displayName')}
              >
                {({ id, describedBy, invalid }) => (
                  <TextInput
                    id={id}
                    describedBy={describedBy}
                    invalid={invalid}
                    value={draft.displayName}
                    maxLength={120}
                    readOnly={!canEdit}
                    onChange={(event) => set('displayName', event.target.value)}
                  />
                )}
              </Field>

              <Field label="Legal name" hint="on invoices and contracts"
                error={errorFor('legalName')}>
                {({ id, describedBy }) => (
                  <TextInput
                    id={id}
                    describedBy={describedBy}
                    value={draft.legalName}
                    maxLength={160}
                    readOnly={!canEdit}
                    onChange={(event) => set('legalName', event.target.value)}
                  />
                )}
              </Field>

              <Field label="Tagline" optional error={errorFor('tagline')}>
                {({ id }) => (
                  <TextInput
                    id={id}
                    value={draft.tagline}
                    maxLength={160}
                    readOnly={!canEdit}
                    onChange={(event) => set('tagline', event.target.value)}
                  />
                )}
              </Field>

              <Field label="Website" error={errorFor('website')}>
                {({ id }) => (
                  <TextInput
                    id={id}
                    value={draft.website}
                    maxLength={200}
                    readOnly={!canEdit}
                    suffix={draft.website
                      ? (
                        <a
                          href={draft.website.startsWith('http') ? draft.website : `https://${draft.website}`}
                          target="_blank"
                          rel="noreferrer"
                          aria-label="Open the website"
                          className="text-text-faint hover:text-text"
                        >
                          <IconExternal size={14} />
                        </a>
                      )
                      : undefined}
                    onChange={(event) => set('website', event.target.value)}
                  />
                )}
              </Field>
            </FormGrid>

            <div className="flex flex-wrap items-center gap-3">
              <span
                aria-hidden
                style={{ background: draft.brandColor }}
                className="h-9 w-9 shrink-0 rounded-[9px] border border-line"
              />
              <span className="w-[130px] shrink-0">
                <TextInput
                  aria-label="Brand colour"
                  value={draft.brandColor}
                  maxLength={7}
                  invalid={Boolean(errorFor('brandColor')) || !validHex}
                  readOnly={!canEdit}
                  onChange={(event) => set('brandColor', event.target.value.toUpperCase())}
                />
              </span>
              <span className="flex min-w-0 flex-col gap-px">
                {/*
                  * Half a hex is the easiest way to lose this page's save, and
                  * it is not the kind of mistake anybody notices making: you
                  * stop typing to look at the swatch. So the line that
                  * normally reads the contrast says this instead.
                  */}
                <span className={cn('text-[12px]',
                  !validHex ? 'text-danger' : textSafe ? 'text-text-muted' : 'text-warn')}>
                  {!validHex
                    ? (
                      <b className="font-bold">
                        {`A colour is six hex digits, like ${DEFAULT_BRAND_COLOR}`}
                      </b>
                    )
                    : (
                      <>
                        <b className="font-bold">{ratio.toFixed(1)} : 1</b>
                        {' on white · '}
                        {textSafe ? 'safe for text' : 'use for bands only'}
                      </>
                    )}
                </span>
                <span className="text-[12px] text-text-muted">
                  Used on the portal, proposals, emails and the crew app accent
                </span>
              </span>
              <span className="ml-auto flex flex-wrap gap-1.5">
                {BRAND_PRESETS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    aria-label={`Use ${preset}`}
                    disabled={!canEdit}
                    style={{ background: preset }}
                    onClick={() => set('brandColor', preset)}
                    className={cn('h-[22px] w-[22px] rounded-[6px] border',
                      preset.toUpperCase() === draft.brandColor.toUpperCase()
                        ? 'border-text ring-2 ring-accent-ring'
                        : 'border-line')}
                  />
                ))}
              </span>
            </div>
          </div>
        </SettingsCard>

        <SettingsCard title="Company contact" sub="the fallback when a job has no branch">
          <div className="flex flex-col gap-3 px-[18px] py-4">
            <FormGrid>
              <Wide>
                <Field label="Address" error={errorFor('addressLine1')}>
                  {({ id }) => (
                    <TextInput
                      id={id}
                      value={draft.addressLine1 ?? ''}
                      maxLength={200}
                      readOnly={!canEdit}
                      onChange={(event) => set('addressLine1', event.target.value)}
                    />
                  )}
                </Field>
              </Wide>

              <Field label="City" error={errorFor('city')}>
                {({ id }) => (
                  <TextInput
                    id={id}
                    value={draft.city ?? ''}
                    maxLength={120}
                    readOnly={!canEdit}
                    onChange={(event) => set('city', event.target.value)}
                  />
                )}
              </Field>

              <div className="grid grid-cols-2 gap-3">
                {/*
                  * A select (`address-state-field.md` §1). This one is the
                  * company's own address, which `documentIdentity` prints on
                  * every quote and invoice that does not use a branch's.
                  */}
                <Field label="State" error={errorFor('state')}>
                  {({ id, describedBy, invalid }) => (
                    <RegionSelect
                      id={id}
                      describedBy={describedBy}
                      invalid={invalid}
                      value={draft.state ?? ''}
                      disabled={!canEdit}
                      onChange={(state) => set('state', state)}
                    />
                  )}
                </Field>
                <Field label="ZIP" error={errorFor('postalCode')}>
                  {({ id }) => (
                    <TextInput
                      id={id}
                      value={draft.postalCode ?? ''}
                      maxLength={20}
                      readOnly={!canEdit}
                      onChange={(event) => set('postalCode', event.target.value)}
                    />
                  )}
                </Field>
              </div>

              <Field label="Phone" error={errorFor('phone')}>
                {({ id }) => (
                  <PhoneInput
                    id={id}
                    value={draft.phone ?? ''}
                    readOnly={!canEdit}
                    onChange={(value) => set('phone', value)}
                  />
                )}
              </Field>

              <Field label="Email" error={errorFor('email')}>
                {({ id }) => (
                  <TextInput
                    id={id}
                    type="email"
                    value={draft.email ?? ''}
                    maxLength={200}
                    readOnly={!canEdit}
                    onChange={(event) => set('email', event.target.value)}
                  />
                )}
              </Field>

              <Field
                label="License / registration #"
                hint="shown on documents when the box below is on"
                error={errorFor('licenseNumber')}
              >
                {({ id, describedBy }) => (
                  <TextInput
                    id={id}
                    describedBy={describedBy}
                    value={draft.licenseNumber ?? ''}
                    maxLength={60}
                    readOnly={!canEdit}
                    onChange={(event) => set('licenseNumber', event.target.value)}
                  />
                )}
              </Field>
            </FormGrid>

            <p className="m-0 text-[12px] leading-[1.45] text-text-muted">
              Branches carry their own address, phone, email and license under{' '}
              <b className="font-semibold">Organization</b>; a document uses the job&apos;s branch
              first and falls back to these.
            </p>
          </div>
        </SettingsCard>

        <SettingsCard title="Documents" sub="proposals, invoices, statements, work orders">
          <div className="flex flex-col gap-3 px-[18px] py-4">
            <FormGrid>
              <Wide>
                <Field label="Header line" optional error={errorFor('docHeader')}>
                  {({ id }) => (
                    <TextInput
                      id={id}
                      value={draft.docHeader}
                      maxLength={200}
                      readOnly={!canEdit}
                      onChange={(event) => set('docHeader', event.target.value)}
                    />
                  )}
                </Field>
              </Wide>
              <Wide>
                <Field
                  label="Footer"
                  hint={`plain text, up to 300 characters · ${draft.docFooter.length} used`}
                  error={errorFor('docFooter')}
                >
                  {({ id, describedBy, invalid }) => (
                    <TextArea
                      id={id}
                      describedBy={describedBy}
                      invalid={invalid}
                      rows={2}
                      value={draft.docFooter}
                      maxLength={300}
                      readOnly={!canEdit}
                      onChange={(event) => set('docFooter', event.target.value)}
                    />
                  )}
                </Field>
              </Wide>
            </FormGrid>

            <div className="grid grid-cols-2 gap-x-4 max-sm:grid-cols-1">
              <CheckRow
                label="Show the license number"
                checked={draft.docShowLicense}
                disabled={!canEdit}
                onChange={(on) => set('docShowLicense', on)}
              />
              <CheckRow
                label="Use the job's branch address and phone"
                checked={draft.docUseBranchContact}
                disabled={!canEdit}
                onChange={(on) => set('docUseBranchContact', on)}
              />
              <CheckRow
                label="Brand colour band on the proposal cover"
                checked={draft.docBrandBand}
                disabled={!canEdit}
                onChange={(on) => set('docBrandBand', on)}
              />
              <CheckRow
                label="Logo on the crew work order"
                checked={draft.docLogoOnWorkOrder}
                disabled={!canEdit}
                onChange={(on) => set('docLogoOnWorkOrder', on)}
              />
            </div>

            <Field label="Paper size" group>
              {({ labelId }) => (
                <Segmented
                  label="Paper size"
                  labelledBy={labelId}
                  value={draft.paperSize}
                  onChange={(value) => set('paperSize', value)}
                  options={[
                    { value: 'letter' as const, label: 'Letter' },
                    { value: 'a4' as const, label: 'A4' },
                  ]}
                />
              )}
            </Field>
          </div>
        </SettingsCard>

        <SettingsCard title="Senders" sub="what emails and texts come from">
          <div className="flex flex-col gap-3 px-[18px] py-4">
            <FormGrid>
              <Field label="Email from name" error={errorFor('emailFromName')}>
                {({ id }) => (
                  <TextInput
                    id={id}
                    value={draft.emailFromName}
                    maxLength={120}
                    placeholder={draft.displayName}
                    readOnly={!canEdit}
                    onChange={(event) => set('emailFromName', event.target.value)}
                  />
                )}
              </Field>

              <Field label="Sending address" hint="your own domain: coming with DNS setup">
                {({ id, describedBy }) => (
                  <ReadonlyInput
                    id={id}
                    describedBy={describedBy}
                    value="no-reply@mail.rowted.app"
                  />
                )}
              </Field>

              <Field label="Reply-to" hint="customer replies land here"
                error={errorFor('replyTo')}>
                {({ id, describedBy }) => (
                  <TextInput
                    id={id}
                    type="email"
                    describedBy={describedBy}
                    value={draft.replyTo ?? ''}
                    maxLength={200}
                    readOnly={!canEdit}
                    onChange={(event) => set('replyTo', event.target.value)}
                  />
                )}
              </Field>

              <Field
                label="Text sender name"
                hint="up to eleven characters · texts go out from our number for now"
                error={errorFor('smsSenderName')}
              >
                {({ id, describedBy }) => (
                  <TextInput
                    id={id}
                    describedBy={describedBy}
                    value={draft.smsSenderName}
                    maxLength={11}
                    readOnly={!canEdit}
                    onChange={(event) => set('smsSenderName', event.target.value)}
                  />
                )}
              </Field>
            </FormGrid>

            <div className={cn('flex flex-wrap items-center gap-2 rounded-card border border-line',
              'bg-surface-alt px-3 py-2.5 text-[12px] text-text-muted')}>
              <span className="shrink-0 text-text-faint"><IconEmail size={14} /></span>
              <span className="min-w-0">
                <b className="font-semibold text-text">Preview:</b>{' '}
                {draft.emailFromName || draft.displayName} &lt;no-reply@mail.rowted.app&gt;
                {draft.replyTo ? ` · reply-to ${draft.replyTo}` : ''}
              </span>
              <button
                type="button"
                disabled={!canEdit || busy}
                onClick={() => void sendTest(setNotice)}
                className="ml-auto shrink-0 text-[12.5px] font-semibold text-accent"
              >
                Send me a test
              </button>
            </div>
          </div>
        </SettingsCard>

        <SettingsCard title="Locale">
          <div className="flex flex-col gap-3 px-[18px] py-4">
            <div className="grid grid-cols-[1.3fr_1fr_1fr_1fr] gap-3 max-lg:grid-cols-2 max-sm:grid-cols-1">
              <Field label="Time zone" hint="branches can differ">
                {({ id, describedBy }) => (
                  <ReadonlyInput
                    id={id}
                    describedBy={describedBy}
                    value={draft.timezone ?? 'Not set'}
                  />
                )}
              </Field>

              <Field label="Currency" hint="set when the account was created">
                {({ id, describedBy }) => (
                  <span className="flex items-center gap-1.5">
                    <span className="shrink-0 text-text-faint"><IconLock size={13} /></span>
                    <ReadonlyInput id={id} describedBy={describedBy} value={draft.currency} />
                  </span>
                )}
              </Field>

              <Field label="Dates" error={errorFor('dateFormat')}>
                {({ id }) => (
                  <SelectInput
                    id={id}
                    label="Dates"
                    value={draft.dateFormat}
                    disabled={!canEdit}
                    onChange={(value) => value && set('dateFormat', value)}
                    options={[
                      { value: 'EEE MMM d, yyyy', label: 'Thu Sep 17, 2026' },
                      { value: 'MMM d, yyyy', label: 'Sep 17, 2026' },
                      { value: 'd MMM yyyy', label: '17 Sep 2026' },
                      { value: 'MM/dd/yyyy', label: '09/17/2026' },
                      { value: 'dd/MM/yyyy', label: '17/09/2026' },
                    ]}
                  />
                )}
              </Field>

              <Field label="Week starts" error={errorFor('weekStart')}>
                {({ id }) => (
                  <SelectInput
                    id={id}
                    label="Week starts"
                    value={String(draft.weekStart)}
                    disabled={!canEdit}
                    onChange={(value) => set('weekStart', value === '0' ? 0 : 1)}
                    options={[
                      { value: '1', label: 'Monday' },
                      { value: '0', label: 'Sunday' },
                    ]}
                  />
                )}
              </Field>
            </div>

            <Field
              label="Measurements"
              group
              hint="changes the catalog's default units, not existing lines"
            >
              {({ labelId }) => (
                <Segmented
                  label="Measurements"
                  labelledBy={labelId}
                  value={draft.units}
                  onChange={(value) => set('units', value)}
                  options={[
                    { value: 'imperial' as const, label: 'Imperial · ft, yd, lb' },
                    { value: 'metric' as const, label: 'Metric · m, m³, kg' },
                  ]}
                />
              )}
            </Field>
          </div>
        </SettingsCard>

      </div>

      <Preview draft={draft} />
    </div>
  )
}

async function sendTest(say: (message: string) => void) {
  try {
    const { to } = await request<{ to: string }>('/company/test-email',
      { method: 'POST', body: {} })
    say(`Sent to ${to}.`)
  } catch (problem) {
    say((problem as { message?: string }).message ?? 'That did not send.')
  }
}

// ---------------------------------------------------------------------------

function LogoSlotView({ slot, label, sub, width, height, url, dark, canEdit, onUploaded }: {
  slot: LogoSlot
  label: string
  sub: string
  width: number
  height: number
  url: string | null
  dark?: boolean
  canEdit: boolean
  onUploaded: () => void
}) {
  const picker = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  async function upload(file: File) {
    setBusy(true)
    setProblem(null)
    try {
      await uploadLogo(slot, file)
      onUploaded()
    } catch (failure) {
      setProblem((failure as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[11.5px] text-text-muted">{label}</span>
      <div
        style={{ width, height }}
        className={cn('flex items-center justify-center overflow-hidden rounded-[12px]',
          'border border-dashed',
          dark ? 'border-white/25 bg-text' : 'border-line-strong bg-surface')}
      >
        {url
          ? <img src={url} alt={label} className="max-h-full max-w-full object-contain" />
          : (
            <span className={cn('px-2 text-center text-[11px]',
              dark ? 'text-line-strong' : 'text-text-faint')}>
              {width === height ? 'Square' : `${width} × ${height}`}
            </span>
          )}
      </div>

      <input
        ref={picker}
        type="file"
        accept="image/png,image/webp,image/jpeg,image/svg+xml"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0]
          if (file) void upload(file)
          event.target.value = ''
        }}
      />

      {canEdit && (
        <button
          type="button"
          disabled={busy}
          onClick={() => picker.current?.click()}
          className="self-start text-[12px] font-semibold text-accent"
        >
          {busy ? 'Uploading…' : 'Replace'}
        </button>
      )}
      <span className="max-w-[180px] text-[11px] leading-[1.35] text-text-faint">
        {problem ?? sub}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------

/**
 * How customers see you — three miniatures at real proportions.
 *
 * Not a screenshot and not a link to one: it re-renders from the form on
 * every keystroke, which is the only way somebody can tell whether a colour
 * works before committing it to every email they will ever send.
 */
function Preview({ draft }: { draft: CompanyProfileView }) {
  const name = draft.displayName || 'Your company'
  const colour = /^#[0-9A-Fa-f]{6}$/.test(draft.brandColor) ? draft.brandColor : DEFAULT_BRAND_COLOR

  return (
    <div className="flex w-[360px] shrink-0 flex-col gap-3.5 max-xl:w-full">
      <SettingsCard title="How customers see you">
        <div className="flex flex-col gap-3 px-[18px] py-4">
          {/* Proposal header */}
          <div className="overflow-hidden rounded-card border border-line">
            {draft.docBrandBand && <div style={{ background: colour }} className="h-1.5" />}
            <div className="flex items-start gap-2.5 p-3">
              {draft.markUrl
                ? <img src={draft.markUrl} alt="" className="h-8 w-8 rounded-[7px] object-contain" />
                : (
                  <span
                    style={{ background: colour }}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[7px] text-[13px] font-bold text-white"
                  >
                    {name.slice(0, 1).toUpperCase()}
                  </span>
                )}
              <span className="flex min-w-0 flex-col gap-px">
                <b className="truncate text-[13px] text-text">{name}</b>
                <span className="truncate text-[10.5px] text-text-muted">
                  {[draft.addressLine1, draft.city, draft.state].filter(Boolean).join(', ')
                    || 'No address yet'}
                  {draft.phone ? ` · ${draft.phone}` : ''}
                </span>
                {draft.docShowLicense && draft.licenseNumber && (
                  <span className="truncate text-[10.5px] text-text-faint">
                    {draft.licenseNumber}
                  </span>
                )}
              </span>
              <span className="ml-auto shrink-0 text-right text-[10.5px] text-text-faint">
                Proposal Q-3102
              </span>
            </div>
          </div>

          {/* Email header */}
          <div className="overflow-hidden rounded-card border border-line p-3">
            <span className="block text-[11px] text-text-muted">
              From <b className="font-semibold text-text">
                {draft.emailFromName || name}
              </b> · no-reply@mail.rowted.app
            </span>
            <span className="mt-1 block text-[12.5px] font-semibold text-text">
              Your proposal from {name} is ready
            </span>
            <span
              style={{ background: colour }}
              className="mt-2 inline-flex h-7 items-center rounded-button px-3 text-[11.5px] font-semibold text-white"
            >
              View proposal
            </span>
          </div>

          {/* Portal sign-in */}
          <div className="flex flex-col items-center gap-2 rounded-card border border-line p-3">
            {draft.markUrl
              ? <img src={draft.markUrl} alt="" className="h-7 w-7 rounded-[6px] object-contain" />
              : (
                <span
                  style={{ background: colour }}
                  className="flex h-7 w-7 items-center justify-center rounded-[6px] text-[12px] font-bold text-white"
                >
                  {name.slice(0, 1).toUpperCase()}
                </span>
              )}
            <b className="text-[12.5px] text-text">{name}</b>
            <span className="text-[10.5px] text-text-muted">
              Customer portal · sign in with your email
            </span>
            <span className="h-7 w-full rounded-field border border-line bg-field" />
            <span
              style={{ background: colour }}
              className="flex h-7 w-full items-center justify-center rounded-button text-[11.5px] font-semibold text-white"
            >
              Continue
            </span>
          </div>
        </div>
      </SettingsCard>

      <SettingsCard title="Where this is used">
        <div className="flex flex-col gap-1.5 px-[18px] py-3.5">
          {[
            'Proposals and invoices — name, address, license, footer',
            'Emails to customers — from name, reply-to, button colour',
            'The customer portal — mark, name, accent',
            'The crew app — accent, and the work order when it carries the logo',
            'Text messages — the sender name, where a carrier allows one',
          ].map((line) => (
            <span key={line} className="flex items-start gap-2 text-[12px] text-text-muted">
              <span className="mt-px shrink-0 text-accent"><IconCheck size={13} /></span>
              {line}
            </span>
          ))}
        </div>
      </SettingsCard>

    </div>
  )
}
